import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Video, Home, Building2, ArrowRight, CheckCircle2 } from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
/**
 * LearningModesSection
 * 3 glossy cards: Online / At home / Learning centres.
 * Each is clickable → /student/search?mode=<mode>
 */
const LearningModesSection = ({ language = 'ar', isDark = false }) => {
  const { localize } = useLanguage();
  const navigate = useNavigate();
  const isAr = language === 'ar';

  const modes = [
    {
      key: 'online',
      icon: Video,
      emoji: '📱',
      title: localize({ ar: 'أونلاين', en: 'Online' }),
      desc: localize({ ar: 'تعلم من أي مكان عبر فيديو مباشر', en: 'Learn from anywhere via live video' }),
      color: 'from-indigo-500 to-blue-600',
      bg: 'bg-indigo-50',
      iconColor: 'text-indigo-600',
      features: [
        localize({ ar: 'فصول افتراضية آمنة', en: 'Safe virtual classrooms' }),
        localize({ ar: 'سبورة ذكية مشتركة', en: 'Shared smart whiteboard' }),
      ],
    },
    {
      key: 'home',
      icon: Home,
      emoji: '🏠',
      title: localize({ ar: 'في المنزل', en: 'At home' }),
      desc: localize({ ar: 'المدرس يأتي إليك بكل راحة وأمان', en: 'Your tutor comes to you — safe and convenient' }),
      color: 'from-amber-500 to-orange-600',
      bg: 'bg-amber-50',
      iconColor: 'text-amber-600',
      features: [
        localize({ ar: 'مدرسون موثقون بالكامل', en: 'Fully verified tutors' }),
        localize({ ar: 'خصوصية تامة للعائلة', en: 'Total family privacy' }),
      ],
    },
    {
      key: 'center',
      icon: Building2,
      emoji: '🏫',
      title: localize({ ar: 'غرف تعليم مجهزة', en: 'Learning centres' }),
      desc: localize({ ar: 'احجز غرفة هادئة ومجهزة للدراسة', en: 'Book a quiet, equipped study room' }),
      color: 'from-teal-500 to-emerald-600',
      bg: 'bg-teal-50',
      iconColor: 'text-teal-600',
      features: [
        localize({ ar: 'إنترنت فائق السرعة', en: 'High-speed internet' }),
        localize({ ar: 'بيئة هادئة للتركيز', en: 'Quiet, focus-friendly' }),
      ],
    },
  ];

  return (
    <section className={`py-10 sm:py-16 lg:py-28 ${isDark ? 'bg-[#09090B]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'} data-testid="learning-modes-section">
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <span className="inline-block text-xs font-bold tracking-widest uppercase text-indigo-600 mb-3">
            {localize({ ar: '3 طرق للتعلم', en: '3 Ways to learn' })}
          </span>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {localize({ ar: 'اختر طريقة التعلم التي تناسبك', en: 'Pick the way you love to learn' })}
          </h2>
          <p className={`mt-3 text-base ${isDark ? 'text-slate-400' : 'text-slate-500'} max-w-2xl mx-auto`}>
            {localize({ ar: 'سواءً كنت مسافراً، في البيت، أو تبحث عن بيئة هادئة — لدينا الحل.', en: 'Whether traveling, at home, or seeking quiet focus — we have you covered.' })}
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {modes.map((mode, i) => {
            const Icon = mode.icon;
            return (
              <motion.button
                type="button"
                key={mode.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                onClick={() => navigate(`/tutors?mode=${mode.key}`)}
                className={`group relative text-start rounded-2xl overflow-hidden border transition-all ${
                  isDark ? 'bg-[#161b22] border-[#30363d] hover:border-indigo-600/50' : 'bg-white border-slate-100 hover:border-indigo-200'
                } shadow-[0_2px_10px_rgba(15,23,42,0.04)] hover:shadow-[0_20px_40px_rgba(79,70,229,0.15)]`}
                data-testid={`mode-card-${mode.key}`}
              >
                {/* Top gradient strip */}
                <div className={`h-1.5 bg-gradient-to-r ${mode.color}`} />

                <div className="p-7 lg:p-8">
                  {/* Icon box */}
                  <div className={`w-14 h-14 rounded-2xl ${isDark ? 'bg-white/5' : mode.bg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-7 h-7 ${mode.iconColor}`} strokeWidth={2.2} />
                  </div>

                  <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    <span className="me-2 text-2xl" aria-hidden>{mode.emoji}</span>
                    {mode.title}
                  </h3>
                  <p className={`text-sm leading-relaxed mb-5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    {mode.desc}
                  </p>

                  {/* Feature bullets */}
                  <ul className="space-y-2 mb-6">
                    {mode.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-xs">
                        <CheckCircle2 className={`w-3.5 h-3.5 ${mode.iconColor} flex-shrink-0 mt-0.5`} />
                        <span className={isDark ? 'text-slate-400' : 'text-slate-600'}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA line */}
                  <div className={`inline-flex items-center gap-1 text-sm font-bold ${mode.iconColor} group-hover:gap-2 transition-all`}>
                    {localize({ ar: 'استعرض المدرسين', en: 'Browse tutors' })}
                    <ArrowRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default LearningModesSection;
